/**
 * フィールド・手札・認識領域の 4 点を決める画面（TetherCam のデバイスの設定 pages/TetherCamDevice.js。Issue #625 / #790）の計算。純関数。
 *
 * synchro-client の config-points（core/field_gen/field_config.py / field_image.py）と**同じ結果**になるように写してある:
 * - 点の並びは TL, TR, BR, BL（元の 1 枚の画素座標）。保存は正規化座標（0〜1）
 * - 矩形モードの状態は (cx, cy, halfW, halfH, angle[rad])。角・回転ハンドル・対角を固定した拡縮・縦横比の固定は
 *   field_image.py の _rect_corners / _rect_state_from_corners / _rotation_handle_point / _resize_rect_corner と同じ式
 * - 補正の出力の大きさ（playmat_width / height）: 4 点モード = プレイマット画像の大きさ、
 *   矩形モード = round(辺) を目標の高さに合わせる（_fit_height）。**丸めは Python の round（偶数への丸め）**
 * - 補正（台形補正）の行き先は OpenCV と同じく [0,0] [W-1,0] [W-1,H-1] [0,H-1]
 */

/** 同梱のプレイマット画像（synchro-client の core/field_gen/data と同じもの・同じ大きさ） */
export const PLAYMATS = {
  field: { src: '/playmats/playmat_field.png', width: 1080, height: 630 },
  hand: { src: '/playmats/playmat_hand.png', width: 1080, height: 315 },
  // 認識領域（Issue #705）: プレイマットではなくカード 1 枚ぶんの切り抜き。大きさは TetherCam の認識枠と同じ
  recog1: { src: null, width: 300, height: 430 },
  recog2: { src: null, width: 300, height: 430 },
};

/** 枠（synchro-client の SLOTS と同じ順） */
export const SLOTS = [
  { fieldType: 'field', player: 1, label: 'フィールド P1' },
  { fieldType: 'field', player: 2, label: 'フィールド P2' },
  { fieldType: 'hand', player: 1, label: '手札 P1' },
  { fieldType: 'hand', player: 2, label: '手札 P2' },
  // 認識領域（画像認識でカードを読む枠。補正サーバーの席ごとの配信にも出る。Issue #705）。フィールド生成には使わない
  { fieldType: 'recog1', player: 1, label: '認識 1 P1' },
  { fieldType: 'recog2', player: 1, label: '認識 2 P1' },
  { fieldType: 'recog1', player: 2, label: '認識 1 P2' },
  { fieldType: 'recog2', player: 2, label: '認識 2 P2' },
];

export const slotKey = (fieldType, player) => `${fieldType}:${player}`;

/** Python の round（ちょうど .5 は偶数へ） */
export function pyRound(x) {
  const f = Math.floor(x);
  const d = x - f;
  if (Math.abs(d - 0.5) < 1e-9) return f % 2 === 0 ? f : f + 1;
  return Math.round(x);
}

/** config-points の既定（画像の端から 10% 内側の矩形） */
export function defaultQuad(w, h) {
  const mx = w * 0.1;
  const my = h * 0.1;
  return [[mx, my], [w - mx, my], [w - mx, h - my], [mx, h - my]];
}

export const toNormalized = (points, w, h) => points.map(([x, y]) => [x / w, y / h]);
export const fromNormalized = (corners, w, h) => corners.map(([u, v]) => [u * w, v * h]);

// --- 矩形モード ---------------------------------------------------------------

export function rectCorners({ cx, cy, halfW, halfH, angle }) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [[-halfW, -halfH], [halfW, -halfH], [halfW, halfH], [-halfW, halfH]]
    .map(([lx, ly]) => [cx + lx * c - ly * s, cy + lx * s + ly * c]);
}

export function rectFromCorners(points) {
  const [p0, p1, p2, p3] = points;
  return {
    cx: (p0[0] + p1[0] + p2[0] + p3[0]) / 4,
    cy: (p0[1] + p1[1] + p2[1] + p3[1]) / 4,
    halfW: Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) / 2,
    halfH: Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) / 2,
    angle: Math.atan2(p1[1] - p0[1], p1[0] - p0[0]),
  };
}

/** 上辺の中央から offset px 外の回転ハンドル */
export function rotationHandle({ cx, cy, halfH, angle }, offset = 30) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const ly = -(halfH + offset);
  return [cx - ly * s, cy + ly * c];
}

/** 回転ハンドルを (x, y) へ動かしたときの角度（上辺が中心から見て上になる向き） */
export function angleFromHandle({ cx, cy }, x, y) {
  return Math.atan2(y - cy, x - cx) + Math.PI / 2;
}

/** 角 cornerIdx を (mx, my) へ。対角を固定して拡縮（角度は変えない）。aspect（幅/高さ）で縦横比を固定 */
export function resizeRectCorner(rect, cornerIdx, mx, my, aspect = null) {
  const { angle } = rect;
  const [ax, ay] = rectCorners(rect)[(cornerIdx + 2) % 4];
  const c1 = Math.cos(-angle);
  const s1 = Math.sin(-angle);
  const dx = mx - ax;
  const dy = my - ay;
  const lx = dx * c1 - dy * s1;
  const ly = dx * s1 + dy * c1;
  const sx = lx >= 0 ? 1 : -1;
  const sy = ly >= 0 ? 1 : -1;
  let halfW;
  let halfH;
  if (aspect) {
    const n = Math.hypot(aspect, 1);
    const dirX = aspect / n;
    const dirY = 1 / n;
    const t = Math.max(Math.abs(lx) * dirX + Math.abs(ly) * dirY, 2 / Math.min(dirX, dirY));
    halfW = (t * dirX) / 2;
    halfH = (t * dirY) / 2;
  } else {
    halfW = Math.max(Math.abs(lx) / 2, 1);
    halfH = Math.max(Math.abs(ly) / 2, 1);
  }
  const c2 = Math.cos(angle);
  const s2 = Math.sin(angle);
  const px = sx * halfW;
  const py = sy * halfH;
  return { cx: ax + px * c2 - py * s2, cy: ay + px * s2 + py * c2, halfW, halfH, angle };
}

/** 縦横比を固定した瞬間に高さを幅に合わせる（config-points の v キーと同じ） */
export function lockAspect(rect, aspect) {
  return aspect ? { ...rect, halfH: rect.halfW / aspect } : rect;
}

// --- 補正の出力の大きさ -----------------------------------------------------------

/** config-points と同じ playmat_width / height */
export function playmatSize(fieldType, points, { rectMode = false, rectMaxHeight = null } = {}) {
  if (!rectMode) {
    const pm = PLAYMATS[fieldType];
    return [pm.width, pm.height];
  }
  const [p0, p1, p2] = points;
  const rw = pyRound(Math.hypot(p1[0] - p0[0], p1[1] - p0[1]));
  const rh = pyRound(Math.hypot(p2[0] - p1[0], p2[1] - p1[1]));
  const target = rectMaxHeight || rh || 1;
  return [Math.max(pyRound(rw * (target / rh)), 1), target];
}

// --- 補正（台形補正）のプレビュー ------------------------------------------------

function solve(a, b) {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c += 1) {
    let p = c;
    for (let r = c + 1; r < n; r += 1) if (Math.abs(m[r][c]) > Math.abs(m[p][c])) p = r;
    if (Math.abs(m[p][c]) < 1e-12) return null;
    [m[c], m[p]] = [m[p], m[c]];
    for (let r = 0; r < n; r += 1) {
      if (r === c) continue;
      const f = m[r][c] / m[c][c];
      for (let k = c; k <= n; k += 1) m[r][k] -= f * m[c][k];
    }
  }
  return m.map((row, i) => row[n] / row[i]);
}

/** 4 点 src → 4 点 dst の射影変換 [a..h]（X = (a x + b y + c) / (g x + h y + 1) …）。潰れていれば null */
export function homographyQuad(src, dst) {
  const A = [];
  const B = [];
  src.forEach(([x, y], i) => {
    const [X, Y] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -x * X, -y * X]); B.push(X);
    A.push([0, 0, 0, x, y, 1, -x * Y, -y * Y]); B.push(Y);
  });
  const H = solve(A, B);
  return H && H.every(Number.isFinite) ? H : null;
}

export function applyH(H, [x, y]) {
  const [a, b, c, d, e, f, g, h] = H;
  const z = g * x + h * y + 1;
  return [(a * x + b * y + c) / z, (d * x + e * y + f) / z];
}

/** 補正の行き先（OpenCV の trapezoid_correction と同じ W-1 / H-1） */
export const warpTarget = (w, h) => [[0, 0], [w - 1, 0], [w - 1, h - 1], [0, h - 1]];

// --- デバイスの座標の文書 ------------------------------------------------------------

/** 画面の状態（枠ごとの画素の点）→ サーバーの doc の entries（TetherCam のデバイスの設定 SetTetherCamDevice。Issue #790）。
 * size はそのデバイスの絵の大きさ、keys は書く枠（省くと全部）。使う（enabled）枠だけ */
export function toEntries(size, slots, keys = null) {
  const entries = [];
  SLOTS.forEach(({ fieldType, player }) => {
    const k = slotKey(fieldType, player);
    const st = slots[k];
    if ((keys && !keys.includes(k)) || !st?.enabled || !st.points) return;
    const [pw, ph] = playmatSize(fieldType, st.points, { rectMode: st.rectMode, rectMaxHeight: st.rectMaxHeight });
    const e = {
      field_type: fieldType, player_number: player, corners: toNormalized(st.points, size.width, size.height),
      playmat_width: pw, playmat_height: ph, rect_mode: !!st.rectMode,
    };
    if (st.rectMode) Object.assign(e, { rect_max_width: st.rectMaxWidth, rect_max_height: st.rectMaxHeight });
    if (fieldType === 'hand' && st.follow) e.follow = true;     // 補正サーバーが枠をカードに追従（Issue #816）
    entries.push(e);
  });
  return entries;
}

export const DEFAULT_RECT_MAX = { field: [1080, 630], hand: [1080, 420], recog1: [300, 430], recog2: [300, 430] };

/** サーバーの doc（無ければ null）→ 画面の状態。その絵の大きさ（w, h）で画素に戻す
 * （無い枠は既定の位置。enabled は defaultEnabled か、無ければフィールドだけ） */
export function fromDoc(doc, w, h, defaultEnabled = null) {
  const slots = {};
  SLOTS.forEach(({ fieldType, player }) => {
    const e = (doc?.entries ?? []).find((x) => x.field_type === fieldType && x.player_number === player);
    const [mw, mh] = DEFAULT_RECT_MAX[fieldType];
    slots[slotKey(fieldType, player)] = e ? {
      enabled: true, points: fromNormalized(e.corners, w, h), rectMode: !!e.rect_mode,
      rectMaxWidth: e.rect_max_width ?? mw, rectMaxHeight: e.rect_max_height ?? mh, follow: !!e.follow,
    } : {
      enabled: defaultEnabled ?? fieldType === 'field', points: defaultQuad(w, h), rectMode: fieldType === 'hand',
      rectMaxWidth: mw, rectMaxHeight: mh,
    };
  });
  return slots;
}

/** 補正サーバーの自動で合わせた結果（/live/cams/autofit の profiles。0〜1）→ 席 seat の枠の編集の状態（画素）。
 * 見つかった役（フィールド・手札・認識 1・2）だけ。今の枠の残りの値は引き継ぐ（Issue #780 → #802） */
export function slotsFromAutofit(profiles, seat, w, h, current = null) {
  const out = {};
  (profiles ?? []).forEach((p) => {
    if (!Array.isArray(p?.corners) || p.corners.length !== 4) return;
    const k = slotKey(p.role, seat);
    out[k] = {
      ...(current?.[k] ?? {}), enabled: true, points: p.corners.map(([u, v]) => [u * w, v * h]), rectMode: !!p.rect_mode,
      ...(p.rect_mode ? { rectMaxWidth: p.rect_max_width ?? p.width, rectMaxHeight: p.rect_max_height ?? p.height } : {}),
    };
  });
  return out;
}

/** 自動で合わせるときに渡す補正後の大きさ（今の枠の矩形の大きさ。決めていなければ補正サーバーの既定） */
export function autofitSizes(slots, seat) {
  const sizes = {};
  ['hand', 'recog1', 'recog2'].forEach((role) => {
    const s = slots?.[slotKey(role, seat)];
    if (s?.rectMaxWidth && s?.rectMaxHeight) sizes[role] = [s.rectMaxWidth, s.rectMaxHeight];
  });
  return sizes;
}
